<template>
  <span>
    <v-card class="mb-0 mt-n2 parametros-v-card elevation-0">
      <v-row justify="start" class="mx-auto mt-0">
        <v-col class="pb-0 pr-5" cols="12" sm="3">
          <div class="mb-2 grey--text body-2 ml-4">
            Município selecionado
          </div>
          <v-autocomplete
            class="pl-4"
            :items="listaMunicipios"
            v-model="municipioSelecionado"
            clearable
            outlined
            dense
          />
        </v-col>
        <v-col class="pb-0" cols="12" sm="3">
          <div
            class="text-subtitle-1 font-weight-medium mb-2 grey--text body-2"
          >
            Ordem Gráficos
          </div>
          <v-select
            :items="listaIndicadoresOrdenacao"
            :value="indicadorOrdenacao"
            @change="setIndicadorOrdenacao"
            :append-icon="iconTipoOrdenacao"
            @click:append="
              tipoOrdenacao === 'asc'
                ? (tipoOrdenacao = 'desc')
                : (tipoOrdenacao = 'asc')
            "
            outlined
            dense
          />
        </v-col>
        <v-col class="pb-0" cols="12" sm="2">
          <div
            class="text-subtitle-1 font-weight-medium mb-2 grey--text body-2 text-no-wrap"
          >
            Quantidade de Municípios
          </div>
          <v-select
            :items="listaQuantidadeMunicipios"
            v-model="quantidadeMunicipios"
            outlined
            dense
            item-text="label"
            item-value="value"
          />
        </v-col>
      </v-row>
    </v-card>

    <v-row dense justify="center" class="mt-3">
      <v-col
        v-for="indicador in indicadores"
        :key="indicador"
        cols="5"
        sm="3"
        md="auto"
        class="flex-md-grow-1"
      >
        <v-card class="my-1 elevation-0">
          <v-card-title>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <h5 v-on="on">{{ indicador | toUpperCase }}</h5>
              </template>
              <span>{{ traducaoIndicadores[indicador] }}</span>
            </v-tooltip>
            <status-evolucao-kpis
              class="ml-1"
              :valorAtual="dadosKpisMunicipioSelecionado[indicador] || 0"
              :valorAnterior="
                dadosKpisMunicipioSelecionadoCompetenciaAnterior[indicador] || 0
              "
            />
          </v-card-title>
          <div
            v-if="
              loadingPorChave.kpisCards || loadingPorChave.kpisCardsAnterior
            "
            class="d-flex align-center justify-center"
            style="height: 50px;"
          >
            <v-progress-circular indeterminate color="primary" size="32" />
          </div>

          <v-card-text v-else>
            <div class="display-3">
              {{
                dadosKpisMunicipioSelecionado[indicador] === null
                  ? "-"
                  : dadosKpisMunicipioSelecionado[indicador]
                    | parseNumberToFloatBrIgnoringNaN
              }}
              <span v-if="['pla', 'pla+e'].includes(indicador)">%</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense justify="center">
      <v-col cols="12" md="9" class="flex-md-grow-1">
        <v-card class="my-1 elevation-0" style="height: 98.5%;">
          <v-card-text>
            <kpis-by-municipio-data-table
              :traducaoIndicadores="traducaoIndicadores"
              :dadosKpisByMunicipio="dadosKpisByMunicipio"
              :loading="loadingPorChave.kpisMunicipio"
            />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="auto" class="flex-md-grow-1">
        <v-card class="my-1 elevation-0">
          <v-card-text>
            <kpis-by-municipio-map
              :dadosKpisByMunicipio="dadosKpisByMunicipio"
              :loading="loadingPorChave.kpisMunicipio"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <dashboard-lno-visao-municipios-cards-kpis-por-municipio
      :dados="dadosKpisByMunicipio"
      :metasKpis="metasKpisMunicipio"
      :municipios="listaMunicipios"
      :tipoOrdenacao="tipoOrdenacao"
      :kpiOrdenacao="indicadorOrdenacao"
      :quantidadeMunicipios="quantidadeMunicipios"
      :exportingOptions="exportingOptions"
      :loading="loadingPorChave.kpisMunicipio"
    />
  </span>
</template>

<script>
import DashboardLnoService from "@/services/DashboardLnoService";

import Highcharts from "highcharts";
import HighchartsNoData from "highcharts/modules/no-data-to-display";
HighchartsNoData(Highcharts);

import exporting from "highcharts/modules/exporting";
import exportData from "highcharts/modules/export-data";
exporting(Highcharts);
exportData(Highcharts);

import dayjs from "dayjs";
import { mapActions, mapState } from "vuex";

export default {
  name: "DashboardLnoVisaoMunicipios",
  components: {
    KpisByMunicipioDataTable: () =>
      import("@/components/dashboardLno/KpisByMunicipioDataTable.vue"),
    KpisByMunicipioMap: () =>
      import("@/components/dashboardLno/KpisByMunicipioMap.vue"),
    StatusEvolucaoKpis: () =>
      import("@/components/dashboardLno/StatusEvolucaoKpis.vue"),
    DashboardLnoVisaoMunicipiosCardsKpisPorMunicipio: () =>
      import(
        "@/components/dashboardLno/DashboardLnoVisaoMunicipiosCardsKpisPorMunicipio.vue"
      ),
  },
  props: {
    competencia: {
      type: String,
      required: true,
    },
    tipoCalculo: {
      type: String,
      required: true,
    },
    tipoLocalizacao: {
      type: String,
      required: true,
    },
    activatedTab: {
      type: Number,
      required: true,
    },
    traducaoIndicadores: {
      type: Object,
      default: {},
    },
    regionalSelecionadaId: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    indicadores: [
      "qlp",
      "pla",
      "pmea15",
      "pmea30",
      "pmea45",
      "pat1",
      "pat2",
      "pat3",
      "pat4",
    ],
    tipoOrdenacao: "desc",
    listaQuantidadeMunicipios: [
      { label: "10 municípios", value: 10 },
      { label: "20 municípios", value: 20 },
      { label: "30 municípios", value: 30 },
      { label: "50 municípios", value: 50 },
      { label: "100 municípios", value: 100 },
      { label: "Todos", value: 0 },
    ],
    quantidadeMunicipios: 30,
    listaMunicipios: [],
    dadosTotais: [],
    dadosTotaisAnteriores: [],
    dadosKpisByMunicipio: [],
    dadosKpisMunicipioCompetenciaAnterior: [],
    metasKpisMunicipio: {},
    exportingOptions: {
      enabled: true,
      buttons: {
        contextButton: {
          align: "right",
          verticalAlign: "bottom",
          menuItems: [
            {
              textKey: "downloadPNG",
              text: "Baixar como PNG",
              onclick: function() {
                this.exportChart();
              },
            },
            {
              separator: true,
            },
            {
              textKey: "downloadCSV",
              text: "Baixar como CSV",
              onclick: function() {
                this.downloadCSV();
              },
            },
            {
              textKey: "downloadXLS",
              text: "Baixar como XLS",
              onclick: function() {
                this.downloadXLS();
              },
            },
          ],
        },
      },
    },
    loadingPorChave: {
      kpisMunicipio: false,
      kpisMunicipioAnterior: false,
      metasKpis: false,
      kpisCards: false,
      kpisCardsAnterior: false,
    },
    isFetching: false,
  }),
  computed: {
    ...mapState("parametrosDashboardLno", ["municipio", "indicadorOrdenacao"]),
    municipioSelecionado: {
      get() {
        return this.municipio;
      },
      set(newValue) {
        this.setMunicipio(newValue);
      },
    },
    dadosKpisMunicipioSelecionado() {
      if (
        this.municipioSelecionado != null &&
        this.municipioSelecionado != undefined
      ) {
        let index = this.dadosKpisByMunicipio.findIndex(
          (dados) => dados.nome_municipio == this.municipioSelecionado,
        );

        return index == -1 ? null : this.dadosKpisByMunicipio[index];
      }

      return this.dadosTotais;
    },
    dadosKpisMunicipioSelecionadoCompetenciaAnterior() {
      if (
        this.municipioSelecionado != null &&
        this.municipioSelecionado != undefined
      ) {
        let index = this.dadosKpisMunicipioCompetenciaAnterior.findIndex(
          (dados) => dados.nome_municipio == this.municipioSelecionado,
        );

        return index == -1
          ? null
          : this.dadosKpisMunicipioCompetenciaAnterior[index];
      }

      return this.dadosTotaisAnteriores;
    },
    listaIndicadoresOrdenacao() {
      return this.indicadores
        .filter((indicador) => indicador !== "ilp")
        .map((indicador) => {
          return {
            text: indicador.toUpperCase(),
            value: indicador,
          };
        });
    },
    iconTipoOrdenacao() {
      if (this.tipoOrdenacao === "asc") return "mdi-sort-reverse-variant";
      else if (this.tipoOrdenacao === "desc") return "mdi-sort-variant";
    },
  },
  mounted() {
    this.getDados();
  },
  methods: {
    ...mapActions("parametrosDashboardLno", [
      "setMunicipio",
      "setIndicadorOrdenacao",
    ]),
    scheduleGetDados() {
      this.getDados();
    },
    getDados() {
      // Apenas atualizado os dados se for a visao selecionada
      if (this.activatedTab !== 2) return;
      if (this.isFetching) {
        return;
      }

      this.isFetching = true;
      this.$emit("updateLoadingDados", true);

      const promises = [
        this.getDadosKpisByMunicipio(),
        this.getDadosKpisByMunicipioCompetenciaAnterior(),
        this.getMetasKpisByMunicipio(),
        this.getDadosTotais(),
        this.getDadosTotaisAnteriores(),
      ];
      Promise.allSettled(promises).then((results) => {
        const hasError = results.some((r) => r.status === "rejected");
        if (hasError) {
          this.$toast.error("Erro ao recuperar parte dos dados.", "", {
            position: "topRight",
          });
        }
      }).finally(() => {
        this.$emit("updateLoadingDados", false);
        this.isFetching = false;
      });
    },
    getDadosKpisByMunicipio() {
      this.loadingPorChave.kpisMunicipio = true;
      return DashboardLnoService.getDadosKpisByMunicipio(
        this.competencia,
        this.tipoCalculo,
        this.regionalSelecionadaId,
        this.tipoLocalizacao,
      )
        .then((res) => {
          this.dadosKpisByMunicipio = res;

          this.listaMunicipios = this.getSortedCategories();
        })
        .finally(() => {
          this.loadingPorChave.kpisMunicipio = false;
        });
    },
    getSortedCategories() {
      const sortedValues =
        this.tipoOrdenacao === "asc"
          ? this.dadosKpisByMunicipio.sort(
              (a, b) =>
                Number(a[this.indicadorOrdenacao] ?? 0) -
                Number(b[this.indicadorOrdenacao] ?? 0),
            )
          : this.dadosKpisByMunicipio.sort(
              (a, b) =>
                Number(b[this.indicadorOrdenacao] ?? 0) -
                Number(a[this.indicadorOrdenacao] ?? 0),
            );

      return sortedValues.map((el) => el.nome_municipio);
    },
    getDadosKpisByMunicipioCompetenciaAnterior() {
      this.loadingPorChave.kpisMunicipioAnterior = true;
      return DashboardLnoService.getDadosKpisByMunicipio(
        dayjs(this.competencia)
          .subtract(1, "month")
          .format("YYYY-MM"),
        this.tipoCalculo,
        this.regionalSelecionadaId,
        this.tipoLocalizacao,
      )
        .then((res) => {
          this.dadosKpisMunicipioCompetenciaAnterior = res;
        })
        .finally(() => {
          this.loadingPorChave.kpisMunicipioAnterior = false;
        });
    },
    getMetasKpisByMunicipio() {
      this.loadingPorChave.metasKpis = true;
      return DashboardLnoService.getMetasKpisMunicipios(
        this.competencia,
        this.regionalSelecionadaId,
      )
        .then((response) => {
          this.metasKpisMunicipio = response;
        })
        .finally(() => {
          this.loadingPorChave.metasKpis = false;
        });
    },
    getDadosTotais() {
      this.loadingPorChave.kpisCards = true;
      return DashboardLnoService.getDadosTotaisIndicadoresLnoMunicipio(
        this.competencia,
        this.competencia,
        this.tipoCalculo,
        this.regionalSelecionadaId,
        this.tipoLocalizacao,
      )
        .then((res) => {
          this.dadosTotais = res;
        })
        .finally(() => {
          this.loadingPorChave.kpisCards = false;
        });
    },
    getDadosTotaisAnteriores() {
      this.loadingPorChave.kpisCardsAnterior = true;
      return DashboardLnoService.getDadosTotaisIndicadoresLnoMunicipio(
        dayjs(this.competencia)
          .subtract(1, "month")
          .format("YYYY-MM"),
        dayjs(this.competencia)
          .subtract(1, "month")
          .format("YYYY-MM"),
        this.tipoCalculo,
        this.regionalSelecionadaId,
        this.tipoLocalizacao,
      )
        .then((res) => {
          this.dadosTotaisAnteriores = res;
        })
        .finally(() => {
          this.loadingPorChave.kpisCardsAnterior = false;
        });
    },
  },
  watch: {
    activatedTab() {
      this.scheduleGetDados();
    },
    competencia() {
      this.scheduleGetDados();
    },
    tipoCalculo() {
      this.scheduleGetDados();
    },
    tipoLocalizacao() {
      this.scheduleGetDados();
    },
    indicadorOrdenacao() {
      this.scheduleGetDados();
    },
    tipoOrdenacao() {
      this.scheduleGetDados();
    },
    regionalSelecionadaId() {
      this.scheduleGetDados();
    },
    quantidadeMunicipios() {
      // A atualização dos charts agora é gerenciada pelo componente filho
    },
  },
};
</script>

<style>
.parametros-v-card {
  box-shadow: 0px 0px 0px !important;
}
</style>
